import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiBriefcaseLight, PiUserLight } from 'react-icons/pi';
import clsx from 'clsx';
import Logo from '../shared/brand/Logo';
import Alert from '../shared/TailwindComponents/Alert';
import Spinner from '../shared/TailwindComponents/Spinner';
import OnboardingStepCard, { ONBOARDING_MAX_WIDTH } from './OnboardingStepCard';
import { Account, chooseAccountType } from '../../services/api/accountService';
import { errorMessage } from '../../services/api/marketplaceService';

/**
 * "Who are you?" - shown once to a signed-in user whose account type is not
 * chosen yet and who did not arrive through `/get-help`. The choices come
 * from the backend (`account.choices`), so a new self-signup type shows up
 * here without a frontend change; known keys get their own wording.
 */
const COPY: Record<string, { title: string; detail: string; icon: React.ReactNode }> = {
  client: {
    title: 'I need a lawyer',
    detail: 'Describe your problem and get proposals from verified lawyers.',
    icon: <PiUserLight className="w-6 h-6" />,
  },
  member: {
    title: 'I work at a law firm',
    detail: 'Set up PONS for your firm or join your colleagues.',
    icon: <PiBriefcaseLight className="w-6 h-6" />,
  },
};

interface AccountTypeStepProps {
  account: Account;
  onChosen: (account: Account) => void;
}

const AccountTypeStep: React.FC<AccountTypeStepProps> = ({ account, onChosen }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Clients first: that is the door most people come through.
  const choices = [...account.choices].sort((a, b) => (a.key === 'client' ? -1 : b.key === 'client' ? 1 : 0));

  const choose = async (key: string) => {
    setSaving(key);
    setError(null);
    try {
      onChosen(await chooseAccountType(key));
    } catch (err) {
      setError(errorMessage(err, t('Something went wrong. Please try again.')));
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4 py-8">
      <div className={clsx('w-full space-y-4', ONBOARDING_MAX_WIDTH)}>
        <OnboardingStepCard
          align="center"
          media={<Logo variant="mark" className="w-12 h-12" />}
          title={t('Welcome to PONS')}
          description={t('How will you use PONS?')}
        >
          <div className="space-y-3">
            {choices.map((choice) => {
              const copy = COPY[choice.key];
              return (
                <button
                  key={choice.key}
                  type="button"
                  disabled={!!saving}
                  onClick={() => choose(choice.key)}
                  className="w-full flex items-start gap-4 rounded-lg border border-hairline bg-surface-sunken/70 p-4 text-left transition-colors hover:border-champagne focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne disabled:opacity-60"
                >
                  <span className="mt-0.5 flex-shrink-0 text-fg-muted">
                    {saving === choice.key ? <Spinner size="glyph" /> : copy?.icon ?? <PiUserLight className="w-6 h-6" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-base font-medium text-fg-strong">
                      {t(copy?.title ?? choice.label ?? choice.key)}
                    </span>
                    {copy && <span className="block text-sm text-fg-muted">{t(copy.detail)}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </OnboardingStepCard>
        {error && <Alert type="error" message={error} />}
      </div>
    </div>
  );
};

export default AccountTypeStep;
